import { useState, useEffect, useMemo } from "react";

// formik components
import { Formik, Form, useFormikContext } from "formik";

// toast notifications
import { toast } from "react-toastify";

// Material UI components
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Page components
import ExportCountriesForm from "./sections/exportCountriesForm";

// API queries
import { useCountryLocCodes } from "api/features/country/queries";
import { useExportEpidemiologyData } from "api/features/export/queries";

// Contexts
import { useUserContext } from "contexts/contextUser";

import initialValues from "../../schemas/exportSchema/initialValues";
import validations from "../../schemas/exportSchema/validations";
import getForm from "../../schemas/exportSchema/form";

function ExportXCountriesForm(): JSX.Element {
  const { data: countryLocCodes } = useCountryLocCodes();
  const { formId, formField } = getForm(countryLocCodes);

  const exportClient = useExportEpidemiologyData();

  const handleSubmit = (values: any, actions: any) => {
    if (exportClient.isPending) {
      actions.setSubmitting(false);
      return;
    }

    const toastId = toast.loading("Exporting data...");

    exportClient.mutate(values, {
      onSuccess: () => {
        toast.update(toastId, {
          render: "Exported successfully!",
          type: "success",
          isLoading: false,
          autoClose: 2500,
        });
      },
      onError: () => {
        toast.update(toastId, {
          render: "Failed to export data.",
          type: "error",
          isLoading: false,
          autoClose: 2500,
        });
      },
      onSettled: () => {
        actions.setSubmitting(false);
      },
    });
  };

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validations}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, isSubmitting }) => {
          const formData = { values, errors, touched, formField };
          const isExporting = isSubmitting || exportClient.isPending;
          return (
            <Form id={formId} autoComplete="off">
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <MDTypography variant="h6">Multiple Countries to export</MDTypography>
                </AccordionSummary>
                <AccordionDetails>
                  <MDTypography variant="h6" sx={{ whiteSpace: "pre-wrap" }}>
                    Export multiple countries to CSV file:
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ whiteSpace: "pre-wrap" }}>
                    Description: Select multiple countries to export data for. The exported data
                    will be in CSV format and will include the selected countries&apos; information.
                    Parameters will be exported.
                  </MDTypography>
                  <ExportCountriesForm formData={formData} />
                  <MDButton
                    disabled={
                      isExporting ||
                      Object.keys(errors).length > 0 ||
                      Object.keys(touched).length === 0
                    }
                    type="submit"
                    variant="gradient"
                    color="dark"
                  >
                    {isExporting ? "Exporting..." : "Export"}
                  </MDButton>
                </AccordionDetails>
              </Accordion>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}

export default ExportXCountriesForm;
